import { Tent, Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT, BOOKING_URL, FLOWER_LOGO } from "../lib/data";

export default function Footer({ scrollTo }) {
  const links = [
    ["Le Camping", "#camping"],
    ["Galerie", "#galerie"],
    ["Hébergements", "#hebergements"],
    ["Randonnées", "route:/randonnees"],
    ["Piscine", "#piscine"],
    ["Services", "#services"],
    ["La Région", "#region"],
    ["Réserver", BOOKING_URL],
  ];

  return (
    <footer className="relative bg-lakedeep border-t border-sand/10 text-sand px-6 sm:px-10 lg:px-16 pt-20 pb-10 grain" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-gold text-lakedeep">
                <Tent size={20} />
              </span>
              <div>
                <div className="font-display font-bold text-2xl">Le Lac aux Oiseaux</div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-gold font-semibold">
                  Flower Camping ★★★★ · Aveyron
                </div>
              </div>
            </div>
            <p className="mt-6 text-sand/60 leading-relaxed max-w-sm">
              Camping familial 4 étoiles au bord du lac de Bannac, à Martiel.
              Entre lac, forêt et bastides, des vacances où confort rime avec simplicité.
            </p>
            <div className="mt-6 inline-flex items-center gap-4 rounded-2xl bg-sand/5 border border-sand/10 px-5 py-3" data-testid="footer-flower-badge">
              <img src={FLOWER_LOGO} alt="Flower Campings" className="h-9 w-auto bg-sand rounded-lg px-2 py-1" />
              <span className="text-sm text-sand/60 leading-snug max-w-[190px]">
                Membre du réseau Flower Campings, l'humain au cœur des vacances
              </span>
            </div>
            <p className="mt-6 font-hand text-3xl text-gold -rotate-2">à bientôt au bord du lac !</p>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold tracking-[0.22em] uppercase text-sand/40">Naviguer</h4>
            <ul className="mt-5 space-y-3">
              {links.map(([label, href]) => (
                <li key={href}>
                  {href.startsWith("http") ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-link-${label.toLowerCase()}`}
                      className="text-sand/70 hover:text-gold transition-colors duration-300"
                    >
                      {label}
                    </a>
                  ) : href.startsWith("route:") ? (
                    <Link
                      to={href.slice(6)}
                      data-testid={`footer-link-${href.slice(7)}`}
                      className="text-sand/70 hover:text-gold transition-colors duration-300"
                    >
                      {label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollTo(href)}
                      data-testid={`footer-link-${href.slice(1)}`}
                      className="text-sand/70 hover:text-gold transition-colors duration-300"
                    >
                      {label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-xs font-bold tracking-[0.22em] uppercase text-sand/40">Nous trouver</h4>
            <ul className="mt-5 space-y-4 text-sand/70">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 shrink-0 text-gold" />
                <span>{CONTACT.address}</span>
              </li>
              <li>
                <a href={CONTACT.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 hover:text-gold transition-colors">
                  <Phone size={16} className="shrink-0 text-gold" />
                  <span>{CONTACT.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="flex items-center gap-3 hover:text-gold transition-colors">
                  <Mail size={16} className="shrink-0 text-gold" />
                  <span>{CONTACT.email}</span>
                </a>
              </li>
            </ul>
            <p className="mt-6 text-sm text-sand/45">
              {CONTACT.season} · Paiements : CB, virement, chèque, espèces, chèques vacances.
            </p>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-sand/10 flex flex-col sm:flex-row justify-between gap-3 text-sm text-sand/40">
          <span>© 2026 Camping Le Lac aux Oiseaux — Martiel, Aveyron. Tous droits réservés.</span>
          <span className="flex gap-5">
            <Link to="/mentions-legales" data-testid="footer-link-mentions-legales" className="hover:text-gold transition-colors duration-300">
              Mentions légales
            </Link>
            <Link to="/politique-confidentialite" data-testid="footer-link-confidentialite" className="hover:text-gold transition-colors duration-300">
              Politique de confidentialité
            </Link>
          </span>
          <span>Camping 4 étoiles au bord du lac de Bannac</span>
        </div>
      </div>
    </footer>
  );
}
